import * as React from 'react';
import Lazy from 'lazy.js';
import Moment from 'moment';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { AGE_GROUPS } from 'Src/consts/school';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, LIMIT } from 'Src/consts/table';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { TEAM_TYPE } from 'Src/consts/team';
import { Loader } from 'Src/components/Loader/Loader';
import { Switch } from 'Src/components/Switch/Switch';
import { TwoPanelEditor } from 'Src/components/TwoPanelEditor/TwoPanelEditor';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Button } from 'Src/components/Button/Button';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { getAge, getGender } from 'Src/helpers/accessor/accessor';
import {
  ColumnDefinition,
  getOrder,
  getServerFieldSectionWhere,
  isFilterExist2,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { getUserGenderArrayConvertedFromEventGender } from 'Src/helpers/tournament/tournament';
import {
  getEventClash,
  getEventTitle,
  getIndividualsMethod,
  getSchoolEventAges,
  getStudentsMethod
} from 'Src/helpers/event/event';
import { getTwoPanelEditorFilters } from 'Src/helpers/twoPanelEditor/twoPanelEditor';
import { getSchoolEventPlayersCount } from 'Src/helpers/service/admin/schoolEvents';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses
} from 'Src/helpers/table/select';
import {
  getSchoolStudentsWithClashesCount,
  getSchoolStudentsWithoutClashes
} from 'Src/helpers/service/admin/schoolTeamPlayers';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getSchoolEvent } from 'Src/helpers/service/admin/event';
import { getSchoolTournamentTeams, addSchoolTournamentTeam } from 'Src/helpers/service/admin/tournamentTeams';
import {
  getSchoolTournamentTeamPlayers,
  updateSchoolTournamentTeamPlayersBatch
} from 'Src/helpers/service/admin/tournamentTeamPlayers';
import { getEventStartDateAndTime } from 'Src/helpers/event/schoolEventViewHelper';
import { updateSchoolEventIndividualsBatch } from 'Src/helpers/service/admin/schoolEventIndividuals';
import { getColorClashes } from 'Src/helpers/cell/cell';
import { SchoolStudentClash } from 'Src/models/schoolStudent';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import { SchoolEventIndividualWithClashes } from 'Src/models/eventIndividuals';
import { EventClash, SchoolEvent, Participant } from 'Src/models/event';
import { formatAgesCompactForSchool, formatTeamAgesCompact, resolveAgeNamingFromSchool } from 'Src/helpers/ageGroups';
import { getTournamentEventParticipationSummary } from 'Src/helpers/service/adminSU/tournamentEventParticipants';
import { SlotAssignment, SlotAssignmentModal } from '../SlotAssignmentModal';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  onCancelClick: () => void;
  onSaveClick: () => void;
}

interface Item {
  id: string;
  permissionId: string;
  form: any;
  house: any;
  gender: string;
  firstName: string;
  lastName: string;
  clashes: {
    status: string;
    fullClashEvents: EventClash[];
    intervalClashEvents: EventClash[];
  };
  playerId?: string;
  eligibilityWarning?: string;
  tournamentEvents?: string;
}

interface State {
  items: Item[];
  itemsSelected: Item[];
  itemsCount: number;

  itemsFilters: any;
  itemsSearch: string;
  isItemsLoading: boolean;
  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;

  isSelectAllItemsChecked: boolean;

  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;

  itemCurrentPage: number;

  itemsAccumulatorInitial: Item[];
  itemsAccumulator: Item[];
  itemsAccumulatorSelected: Item[];
  itemsAccumulatorCount: number;

  itemsAccumulatorFilters: any;
  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;

  isSelectAllItemsAccumulatorChecked: boolean;

  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;

  isLoading: boolean;

  itemsAddBuffer: Item[];
  itemsRemoveBuffer: Item[];

  forms: SchoolForm[];
  houses: SchoolHouse[];

  studentEventsModalText: string;
  isStudentEventsModalOpen: boolean;

  participants: Participant[];
  tournamentTeams: TournamentTeam[];

  isShowYounger: boolean;

  isMaxPlayersModalOpen: boolean;
  maxPlayersModalMessage: string;

  isHelpModalOpen: boolean;
  isExitConfirmationModalOpen: boolean;
  clashEventsById: Record<string, SchoolEvent>;
  participationLimitWarnings: string[];
  isCheckingParticipationLimits: boolean;
  slotCandidates: Item[];
}

const getEligibleStudentNameCell = (item: Item): React.ReactNode => (
  <div className="tournamentEventParticipants__studentName">
    <span>{item.firstName}</span>
    {item.tournamentEvents && <small title={item.tournamentEvents}>Also in: {item.tournamentEvents}</small>}
  </div>
);

const ITEMS_ACCUMULATOR_COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender
  }
];

const ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'Clashes',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    accessor: ['firstName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name']
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getGender
  }
];

const ITEMS_COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'firstName',
    cell: getEligibleStudentNameCell,
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  }
];

const ITEMS_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  {
    text: 'Clashes',
    field: 'clashes',
    cell: getColorClashes,
    type: FILTER_TYPE.NONE,
    isSort: false
  },
  {
    text: 'Name',
    field: 'firstName',
    cell: getEligibleStudentNameCell,
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Surname',
    field: 'lastName',
    accessor: ['lastName'],
    type: FILTER_TYPE.TEXT,
    isSort: false
  },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name']
  },
  {
    text: 'Age',
    field: 'ages',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getAge
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name']
  }
];

const DEFAULT_INDIVIDUAL_PLAYERS_ORDER = 'firstName ASC';

export class TournamentSchoolIndividualManager extends React.Component<Props, State> {
  private itemsSearchRequestId = 0;
  private clashEventIdsLoading = new Set<string>();
  private tournamentEventsByStudentId: Record<string, string> = {};
  private tournamentStudentIds: string[] = [];

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      itemsCount: 0,
      itemsSelected: [],

      itemsFilters: {},
      itemsSearch: '',
      isItemsLoading: false,
      isShowItemsFilter: false,
      isItemsFiltered: false,

      isSelectAllItemsChecked: false,

      sortItemsDirection: '',
      sortItemsColumnsName: '',

      itemCurrentPage: 1,

      itemsAccumulatorInitial: [],
      itemsAccumulator: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],

      itemsAccumulatorFilters: {},
      isShowItemsAccumulatorFilter: false,
      isItemsAccumulatorFiltered: false,

      isSelectAllItemsAccumulatorChecked: false,

      sortItemsAccumulatorDirection: '',
      sortItemsAccumulatorColumnsName: '',

      isLoading: true,

      itemsAddBuffer: [],
      itemsRemoveBuffer: [],

      forms: [],
      houses: [],
      studentEventsModalText: '',
      isStudentEventsModalOpen: false,

      participants: [],
      tournamentTeams: [],

      isShowYounger: false,

      isMaxPlayersModalOpen: false,
      maxPlayersModalMessage: '',

      isHelpModalOpen: false,
      isExitConfirmationModalOpen: false,
      clashEventsById: {},
      participationLimitWarnings: [],
      isCheckingParticipationLimits: false,
      slotCandidates: []
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.items !== this.state.items || prevState.itemsAccumulator !== this.state.itemsAccumulator) {
      this.loadClashEventNames([...this.state.items, ...this.state.itemsAccumulator]);
    }
  }

  loadClashEventNames = (students: Item[]) => {
    const { user } = this.props;
    const missingIds = Array.from(
      new Set(
        students.reduce<string[]>((ids, student) => {
          const events = [...(student.clashes.fullClashEvents || []), ...(student.clashes.intervalClashEvents || [])];
          events.forEach((event: any) => {
            const id = String(event._id || event.id || '');
            if (id && !this.state.clashEventsById[id] && !this.clashEventIdsLoading.has(id)) ids.push(id);
          });
          return ids;
        }, [])
      )
    );
    if (!missingIds.length) return;
    missingIds.forEach(id => this.clashEventIdsLoading.add(id));
    Promise.all(missingIds.map(id => getSchoolEvent(user, id).then(event => ({ id, event }))))
      .then(entries => {
        entries.forEach(({ id }) => this.clashEventIdsLoading.delete(id));
        this.setState(state => ({
          clashEventsById: entries.reduce(
            (result, { id, event }) => ({ ...result, [id]: event }),
            state.clashEventsById
          )
        }));
      })
      .catch(() => missingIds.forEach(id => this.clashEventIdsLoading.delete(id)));
  };

  getDefaultItemsFilter(event: SchoolEvent, forms: SchoolForm[] = this.state.forms) {
    const { gender, ages } = event;
    const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);

    return {
      firstName: '',
      lastName: '',
      gender: genderArrayConverted,
      forms: [],
      ages: ages
    };
  }

  getDefaultItemsAccumulatorFilter(event: SchoolEvent) {
    const { gender, ages } = event;
    const genderArrayConverted = getUserGenderArrayConvertedFromEventGender(gender);

    return {
      firstName: '',
      lastName: '',
      gender: genderArrayConverted,
      forms: [],
      ages: ages
    };
  }

  getEventId(): string {
    const { event } = this.props;
    const { id } = event;
    return id;
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    const { user, event } = this.props;
    const { tournamentId } = event;

    const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
    const isShowItemsFilter = isFilterExist2(itemsFilters);
    const isItemsFiltered = isFilterExist2(itemsFilters);

    const itemsAccumulatorFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);
    const isShowItemsAccumulatorFilter = isFilterExist2(itemsAccumulatorFilters);
    const isItemsAccumulatorFiltered = isFilterExist2(itemsAccumulatorFilters);

    const tournamentTeamsPromise = getSchoolTournamentTeams(user, tournamentId);
    const formsPromise = getAllForms(user);
    const housesPromise = getAllHouses(user);

    Promise.all([tournamentTeamsPromise, formsPromise, housesPromise])
      .then(([tournamentTeams, forms, houses]) => {
        const teamPlayersPromise = Promise.all(
          (tournamentTeams || []).map(team =>
            getSchoolTournamentTeamPlayers(
              user,
              tournamentId,
              team.id,
              { limit: DEFAULT_LIMIT, skip: DEFAULT_SKIP },
              false
            )
          )
        ).then(teamPlayers => teamPlayers.reduce((allPlayers, players) => allPlayers.concat(players || []), []));

        return teamPlayersPromise.then(tournamentPlayers => {
          const tournamentStudentIds = Array.from(
            new Set((tournamentPlayers || []).map(player => String(player.userId)).filter(Boolean))
          );
          return getTournamentEventParticipationSummary(user, tournamentId, tournamentStudentIds)
            .catch(() => [])
            .then(participationSummary => ({
              tournamentTeams,
              forms,
              houses,
              tournamentPlayers,
              participationSummary
            }));
        });
      })
      .then(({ tournamentTeams, forms, houses, tournamentPlayers, participationSummary }) => {
        this.tournamentStudentIds = Array.from(
          new Set((tournamentPlayers || []).map(player => String(player.userId)).filter(Boolean))
        );
        const groupedEvents = participationSummary.reduce((eventsByStudentId, participation) => {
          const studentId = String(participation.userId);
          const eventName = [participation.sportName, participation.shortDescription]
            .map(value => String(value || '').trim())
            .filter(Boolean)
            .join(' · ');
          const existing = eventsByStudentId[studentId] || [];
          if (eventName && !existing.includes(eventName)) existing.push(eventName);
          eventsByStudentId[studentId] = existing;
          return eventsByStudentId;
        }, {} as Record<string, string[]>);

        const eventsByUserId = Object.keys(groupedEvents).reduce(
          (result, studentId) => ({ ...result, [studentId]: groupedEvents[studentId].join(', ') }),
          {}
        );
        this.tournamentEventsByStudentId = (tournamentPlayers || []).reduce((result, player) => {
          const events = eventsByUserId[String(player.userId)];
          if (!events) return result;
          result[String(player.userId)] = events;
          if (player.permissionId) result[String(player.permissionId)] = events;
          return result;
        }, { ...eventsByUserId } as Record<string, string>);
        const defaultItemsFilter = this.getDefaultItemsFilter(event, forms);
        const defaultItemsAccumulatorFilter = this.getDefaultItemsAccumulatorFilter(event);

        this.setState({
          tournamentTeams,
          forms,
          houses,
          itemsFilters: defaultItemsFilter,
          itemsAccumulatorFilters: defaultItemsAccumulatorFilter
        });

        return this.getItemsAndItemsAccumulator();
      })
      .then(({ playersInitial, players, playersCount, students, studentsCount }) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        const playersNormalized = this.getPlayersNormalized(players);
        const playersInitialNormalized = this.getPlayersNormalized(playersInitial);

        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          isShowItemsFilter: isShowItemsFilter,
          isItemsFiltered: isItemsFiltered,

          itemsAccumulatorInitial: playersInitialNormalized,
          itemsAccumulator: playersNormalized,
          itemsAccumulatorCount: playersCount.count,
          isShowItemsAccumulatorFilter: isShowItemsAccumulatorFilter,
          isItemsAccumulatorFiltered: isItemsAccumulatorFiltered,

          isLoading: false
        });
      });
  }

  getPlayersNormalized(players: SchoolEventIndividualWithClashes[]): Item[] {
    return players.map(player => {
      const { permissionId, form, gender, firstName, lastName, userId, id, house, clashes } = player;
      return {
        permissionId,
        form,
        house,
        gender: gender ?? '',
        firstName,
        lastName,
        clashes,
        id: userId,
        playerId: id,
        tournamentEvents:
          this.tournamentEventsByStudentId[String(userId)] ||
          this.tournamentEventsByStudentId[String(permissionId)] ||
          '',
        eligibilityWarning: this.getEligibilityWarning(form)
      };
    });
  }

  getStudentsNormalized(students: SchoolStudentClash[]): Item[] {
    return students.map(student => {
      const { permissionId, form, gender, firstName, lastName, id, house, clashes } = student;

      return {
        permissionId,
        form,
        house,
        gender: gender ?? '',
        firstName,
        lastName,
        clashes,
        id,
        tournamentEvents:
          this.tournamentEventsByStudentId[String(id)] ||
          this.tournamentEventsByStudentId[String(permissionId)] ||
          '',
        eligibilityWarning: this.getEligibilityWarning(form)
      };
    });
  }

  getEligibilityWarning(form: any): string {
    const { event, user } = this.props;
    const studentAge = Number(propz.get(form, ['age']));
    if (!Number.isFinite(studentAge) || event.ages.includes(studentAge)) return '';
    return `Age: event is ${formatAgesCompactForSchool(
      event.ages,
      user.activeSchool
    )}, student is ${formatAgesCompactForSchool([studentAge], user.activeSchool)}`;
  }

  getFormsFiltered(ages: number[], forms: SchoolForm[], isStrictEqual: boolean): SchoolForm[] {
    const maxAge = Math.max(...ages);
    const minAge = Math.min(...ages);

    switch (true) {
      case isStrictEqual && ages.length === 1:
        return forms.filter(form => form.age === minAge);
      case !isStrictEqual && ages.length === 1:
        return forms.filter(form => form.age <= maxAge);
      case isStrictEqual && ages.length > 1:
        return forms.filter(form => ages.some(age => form.age === age));
      case !isStrictEqual && ages.length > 1:
        return forms.filter(form => ages.some(age => form.age <= age));
      default:
        return [];
    }
  }

  onShowYoungerChange = () => {
    const { user, event } = this.props;
    const { isShowYounger, itemsAccumulatorInitial, itemsAddBuffer, forms, itemsFilters } = this.state;
    const { ages } = event;

    const nextIsShowYounger = !isShowYounger;
    const formsFiltered = this.getFormsFiltered(ages, forms, !nextIsShowYounger);
    const formIds = formsFiltered.map(form => form.id);

    const nextItemsFilters = {
      ...itemsFilters,
      forms: formIds,
      ages: ages
    };

    this.setState({
      isShowYounger: nextIsShowYounger,
      itemsFilters: nextItemsFilters
    });

    const allItems = [...itemsAccumulatorInitial, ...itemsAddBuffer];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();

    const where = {
      eventId: event.id,
      gender: { $in: nextItemsFilters.gender },
      _id: { $in: this.tournamentStudentIds, $nin: ninIds },
      formId: { $in: formIds }
    };

    const filter = {
      where,
      limit: LIMIT,
      skip: 0,
      order: DEFAULT_INDIVIDUAL_PLAYERS_ORDER
    };

    const method = getStudentsMethod(user);
    Promise.all([method(user, filter), getSchoolStudentsWithClashesCount(user, filter)]).then(
      ([students, studentsCount]) => {
        const studentsNormalized = this.getStudentsNormalized(students);
        this.setState({
          items: studentsNormalized,
          itemsCount: studentsCount.count,
          itemCurrentPage: 1,
          itemsSelected: [],
          itemsAccumulatorSelected: []
        });
      }
    );
  };

  getItemsFilter(page, itemsAccumulator, itemsAddBuffer, isAllItems = false) {
    const { itemsFilters, forms, itemsSearch } = this.state;
    const { event } = this.props;

    const { startTime, endTime, players, id } = event;

    const {
      gender: itemsFiltersGender,
      ages: itemsFiltersAges = [],
      firstName: itemsFiltersFirstName = '',
      lastName: itemsFiltersLastName = '',
      forms: itemsFiltersForms = [],
      houses: itemsFiltersHouses = []
    } = itemsFilters;

    let where = {
      eventId: id,
      gender: {
        $in: itemsFiltersGender
      }
    };

    const allItems = [...itemsAccumulator, ...itemsAddBuffer];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);
    propz.set(where, ['_id', '$in'], this.tournamentStudentIds);

    if (itemsFiltersForms.length > 0) {
      propz.set(where, ['formId', '$in'], itemsFiltersForms);
    } else {
      const ages = itemsFiltersAges.length > 0 ? itemsFiltersAges : AGE_GROUPS.ENGLISH.map((age, index) => index);
      const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

      if (formIds.length > 0) {
        propz.set(where, ['formId', '$in'], formIds);
      }
    }

    if (itemsFiltersHouses.length > 0) {
      propz.set(where, ['houseId', '$in'], itemsFiltersHouses);
    }

    let and: any[] = [];

    if (itemsFiltersFirstName.length > 0) {
      and.push({ firstName: { like: itemsFiltersFirstName, options: 'i' } });
    }

    if (itemsFiltersLastName.length > 0) {
      and.push({ lastName: { like: itemsFiltersLastName, options: 'i' } });
    }

    if (itemsSearch.trim().length > 0) {
      const searchValue = itemsSearch.trim();
      and.push({
        $or: [{ firstName: { like: searchValue, options: 'i' } }, { lastName: { like: searchValue, options: 'i' } }]
      });
    }

    if (and.length > 0) {
      propz.set(where, ['$and'], and);
    }

    // copy-paste from old frontend
    const startTimeDate = new Date(startTime ?? '');
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    if (!startTime || !endTime) return;

    const endTimeDate = new Date(endTime ?? '');
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: isAllItems ? DEFAULT_LIMIT : LIMIT,
      skip: isAllItems ? 0 : (page - 1) * LIMIT,
      order: DEFAULT_INDIVIDUAL_PLAYERS_ORDER
    };

    return filter;
  }

  getItemsAndItemsAccumulator() {
    const { user } = this.props;
    const { itemsAccumulatorFilters, forms } = this.state;

    const options = { forms };
    const where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);

    const eventId = this.getEventId();

    const getPlayersInitialPromise = getIndividualsMethod(user)(user, eventId, { where });
    const getPlayersPromise = getIndividualsMethod(user)(user, eventId, { where });
    const getPlayersCountPromise = getSchoolEventPlayersCount(user, eventId, where);

    let playersInitial, players, playersCount;

    return Promise.all([getPlayersInitialPromise, getPlayersPromise, getPlayersCountPromise])
      .then(([_playersInitial, _players, _playersCount]) => {
        playersInitial = _playersInitial;
        players = _players;
        playersCount = _playersCount;

        const playersInitialNormalized = this.getPlayersNormalized(playersInitial);

        const filter = this.getItemsFilter(1, playersInitialNormalized, []);

        const method = getStudentsMethod(user);
        const getStudentsPromise = method(user, filter);
        const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

        return Promise.all([getStudentsPromise, getStudentsCountPromise]);
      })
      .then(([students, studentsCount]) => {
        return {
          playersInitial,
          players,
          playersCount,
          students,
          studentsCount
        };
      });
  }

  onItemClick = (index: number): void => {
    const { items, itemsSelected } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = itemsSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: [],
      itemsSelected: selectedItemsUpdated
    });
  };

  onItemAccumulatorClick = (index: number): void => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;
    const selectedItem = itemsAccumulator[index];

    const selectedItemIndex = itemsAccumulatorSelected.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...itemsAccumulatorSelected];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      itemsAccumulatorSelected: selectedItemsUpdated,
      itemsSelected: []
    });
  };

  stageSelectedItems = () => {
    const {
      itemsSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const { user, event } = this.props;

    const maxPlayers =
      propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
      propz.get(event, ['tournament', 'maxPlayersPerTeam']);

    if (typeof maxPlayers === 'number' && maxPlayers > 0) {
      const currentCount = itemsAccumulator.length;
      const tryingToAdd = itemsSelected.length;

      if (currentCount + tryingToAdd > maxPlayers) {
        this.setState({
          slotCandidates: [...itemsSelected]
        });
        return;
      }
    }

    const removedIds = new Set(itemsRemoveBuffer.map(item => String(item.id)));
    const nextItemsRemoveBuffer = itemsRemoveBuffer.filter(
      removed => !itemsSelected.some(selected => String(selected.id) === String(removed.id))
    );
    const nextItemsAddBuffer = [...itemsAddBuffer, ...itemsSelected.filter(item => !removedIds.has(String(item.id)))];

    const itemsToAddNormalized: Item[] = itemsSelected.map(item => ({
      id: item.id,
      playerId: propz.get(
        itemsRemoveBuffer.find(removed => String(removed.id) === String(item.id)),
        ['playerId'],
        ''
      ),
      permissionId: item.permissionId,
      form: item.form,
      house: item.house,
      gender: item.gender,
      firstName: item.firstName,
      lastName: item.lastName,
      clashes: item.clashes
    }));

    const existingIds = new Set((itemsAccumulator || []).map(x => String(x.id)));
    const nextItemsAccumulator = [
      ...itemsAccumulator,
      ...itemsToAddNormalized.filter(x => !existingIds.has(String(x.id)))
    ];

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        itemsCount: studentsCount.count,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsRemoveBuffer: nextItemsRemoveBuffer,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false
      });
    });
  };

  onAddClick = () => {
    const { event, user } = this.props;
    const selected = [...this.state.itemsSelected];
    const tournamentId = event.tournamentId || propz.get(event, ['tournament', 'id'], '');
    const limit = Number(propz.get(event, ['tournament', 'maxIndividualEventsPerStudent'], 0));
    const isEnforced = propz.get<any>(event, ['tournament', 'isEventParticipantLimitEnforced'], true) !== false;

    if (!selected.length || !tournamentId || !isEnforced || limit <= 0) {
      this.stageSelectedItems();
      return;
    }

    this.setState({ isCheckingParticipationLimits: true });
    getTournamentEventParticipationSummary(
      user,
      tournamentId,
      selected.map(item => item.id)
    )
      .then(summary => {
        const warnings = selected.reduce((messages, student) => {
          const otherEvents = (summary || []).filter(
            item => item.userId === student.id && item.eventId !== event.id && !item.isRelay
          );
          if (otherEvents.length < limit) return messages;
          messages.push(
            `${student.firstName} ${student.lastName} is already in ${otherEvents.length} individual ${
              otherEvents.length === 1 ? 'event' : 'events'
            } and may take part in no more than ${limit}.`
          );
          return messages;
        }, [] as string[]);
        this.setState(
          { participationLimitWarnings: warnings, isCheckingParticipationLimits: false },
          this.stageSelectedItems
        );
      })
      .catch(() =>
        this.setState({ participationLimitWarnings: [], isCheckingParticipationLimits: false }, this.stageSelectedItems)
      );
  };

  onRemoveClick = () => {
    const {
      itemsAccumulatorSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const { user } = this.props;

    const selectedIds = new Set((itemsAccumulatorSelected || []).map(x => String(x.id)));
    const addBufferIds = new Set((itemsAddBuffer || []).map(x => String(x.id)));

    const selectedExistingItems = (itemsAccumulatorSelected || []).filter(x => !addBufferIds.has(String(x.id)));

    const nextItemsAddBuffer = (itemsAddBuffer || []).filter(x => !selectedIds.has(String(x.id)));

    const existingRemoveIds = new Set((itemsRemoveBuffer || []).map(x => String(x.id)));
    const nextItemsRemoveBuffer = [
      ...(itemsRemoveBuffer || []),
      ...selectedExistingItems.filter(x => !existingRemoveIds.has(String(x.id)))
    ];

    const nextItemsAccumulatorInitial = (itemsAccumulatorInitial || []).filter(
      accItem => !nextItemsRemoveBuffer.some(rem => String(rem.id) === String(accItem.id))
    );

    const nextItemsAccumulator = (itemsAccumulator || []).filter(accItem => !selectedIds.has(String(accItem.id)));

    const filter = this.getItemsFilter(itemCurrentPage, nextItemsAccumulatorInitial, nextItemsAddBuffer);
    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isSelectAllItemsChecked: false,
        itemsAddBuffer: nextItemsAddBuffer,
        itemsRemoveBuffer: nextItemsRemoveBuffer,
        itemsAccumulatorInitial: nextItemsAccumulatorInitial,
        itemsAccumulator: nextItemsAccumulator,
        itemsAccumulatorCount: nextItemsAccumulator.length,
        participationLimitWarnings: this.state.participationLimitWarnings.filter(
          message => !itemsAccumulatorSelected.some(item => message.startsWith(`${item.firstName} ${item.lastName} `))
        )
      });
    });
  };

  onSlotAssignmentConfirm = (assignments: SlotAssignment[]) => {
    const { itemsAccumulator, itemsAddBuffer, itemsRemoveBuffer } = this.state;
    const assignedIds = new Set(assignments.map(assignment => String(assignment.student.userId)));
    const replacedPlayerIds = new Set(
      assignments.map(assignment => String(assignment.replacingPlayerId || '')).filter(Boolean)
    );
    const replaced = itemsAccumulator.filter(item => replacedPlayerIds.has(String(item.playerId || item.id)));
    const stagedItemIds = new Set(itemsAddBuffer.map(item => String(item.id)));
    const persistedReplaced = replaced.filter(
      item => !stagedItemIds.has(String(item.id)) && typeof item.playerId === 'string' && item.playerId.length > 0
    );
    const candidates = this.state.slotCandidates.filter(item => assignedIds.has(String(item.id)));
    const nextAccumulator = [
      ...itemsAccumulator.filter(item => !replaced.some(value => value.id === item.id)),
      ...candidates.filter(item => !itemsAccumulator.some(value => value.id === item.id))
    ];
    const nextRemoveBuffer = [
      ...itemsRemoveBuffer,
      ...persistedReplaced.filter(item => !itemsRemoveBuffer.some(value => value.id === item.id))
    ];
    const nextAddBuffer = [
      ...itemsAddBuffer.filter(item => !replaced.some(value => value.id === item.id)),
      ...candidates.filter(item => !itemsAddBuffer.some(value => value.id === item.id))
    ];
    const assignedNames = new Set(candidates.map(item => `${item.firstName} ${item.lastName} `));

    this.setState({
      itemsAccumulator: nextAccumulator,
      itemsAccumulatorCount: nextAccumulator.length,
      itemsAccumulatorSelected: [],
      itemsSelected: [],
      itemsAddBuffer: nextAddBuffer,
      itemsRemoveBuffer: nextRemoveBuffer,
      slotCandidates: [],
      participationLimitWarnings: this.state.participationLimitWarnings.filter(message =>
        Array.from(assignedNames).some(name => message.startsWith(name))
      )
    });
  };

  onClickSave = () => {
    if (this.state.participationLimitWarnings.length || this.state.isCheckingParticipationLimits) return;
    const { user, event, onSaveClick } = this.props;
    const { itemsAddBuffer, itemsRemoveBuffer, tournamentTeams } = this.state;
    const { activeSchoolId } = user;
    const { tournamentId } = event;

    const currentSchoolTeam = tournamentTeams.find(team => team.schoolId === activeSchoolId);
    const isCurrentSchoolTeamExist = typeof currentSchoolTeam !== 'undefined';

    const tournamentTeamsPlayers = propz.get(currentSchoolTeam, ['players'], []);
    const tournamentTeamsPlayersIds = tournamentTeamsPlayers.map(player => player.userId);

    const studentsToAddToTeam = itemsAddBuffer.filter(item => !tournamentTeamsPlayersIds.includes(item.id));
    const isStudentsToAddToTeamExist = studentsToAddToTeam.length > 0;

    this.setState({
      isLoading: true
    });

    const eventId = this.getEventId();

    const data = {
      add: itemsAddBuffer.map(item => ({ userId: item.id, permissionId: item.permissionId })),
      remove: itemsRemoveBuffer
        .map(item => item.playerId)
        .filter((id): id is string => typeof id === 'string' && /^[a-f\d]{24}$/i.test(id))
    };

    if (!tournamentId) return;

    updateSchoolEventIndividualsBatch(user, eventId, data)
      .then(() => {
        switch (true) {
          case isCurrentSchoolTeamExist:
            if (isStudentsToAddToTeamExist) {
              const addToTeamData = {
                add: studentsToAddToTeam.map(item => ({ userId: item.id, permissionId: item.permissionId })),
                remove: []
              };

              return updateSchoolTournamentTeamPlayersBatch(user, tournamentId, currentSchoolTeam.id, addToTeamData);
            }
            break;
          case !isCurrentSchoolTeamExist:
            return this.createDefaultTeam();
        }
      })
      .then(newTeam => {
        if (!isCurrentSchoolTeamExist) {
          const addToTeamData = {
            add: studentsToAddToTeam.map(item => ({ userId: item.id, permissionId: item.permissionId })),
            remove: []
          };

          return updateSchoolTournamentTeamPlayersBatch(user, tournamentId, newTeam.id, addToTeamData);
        }
      })
      .then(() => {
        onSaveClick();
      });
  };

  createDefaultTeam = () => {
    const { event, user } = this.props;
    const { activeSchoolId: schoolId } = user;
    const { tournament, tournamentId } = event;
    const { gender, ages, sportId } = tournament;
    const tournamentName = propz.get(tournament, ['name'], '');
    const schoolName = propz.get(user, ['activeSchoolName'], '');
    const name = `${schoolName} ${tournamentName}`;

    if (!tournamentId) return;

    const teamData = {
      name,
      ages,
      gender,
      sportId,
      schoolId,
      teamType: TEAM_TYPE.ADHOC,
      isAutoEnrolledTeam: true,
      players: []
    };

    return addSchoolTournamentTeam(user, tournamentId, teamData);
  };

  onItemsFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (filterField === 'forms') {
      nextFilters.ages = [];
    }

    if (filterField === 'ages') {
      nextFilters.forms = [];
    }

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      if (!filter) return;
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsFilters: nextFilters
    });
  };

  onItemsAccumulatorFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.itemsAccumulatorFilters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = ITEMS_COLUMNS.find(col => col.field === filterField);
      if (!filter) return;
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value: string[] = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      itemsAccumulatorFilters: nextFilters
    });
  };

  onItemsFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsFilter } = this.state;

    this.setState({
      isShowItemsFilter: !isShowItemsFilter
    });
  };

  onItemsAccumulatorFilterClick = (event): void => {
    event.preventDefault();

    const { isShowItemsAccumulatorFilter } = this.state;

    this.setState({
      isShowItemsAccumulatorFilter: !isShowItemsAccumulatorFilter
    });
  };

  itemsSetCurrentPageParams = (currentPage: number): void => {
    const { user } = this.props;
    const { itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const filter = this.getItemsFilter(currentPage, itemsAccumulator, itemsAddBuffer);

    const method = getStudentsMethod(user);

    method(user, filter).then(students => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        itemCurrentPage: currentPage,
        items: studentsNormalized,
        isLoading: false
      });
    });
  };

  itemsOnApplyFilterClick = (isSilent = false) => {
    const { itemsAccumulator, itemsAddBuffer } = this.state;
    const requestId = ++this.itemsSearchRequestId;

    if (isSilent) {
      this.setState({ isItemsLoading: true });
    } else {
      this.setState({ isLoading: true });
    }

    const filter = this.getItemsFilter(1, itemsAccumulator, itemsAddBuffer);

    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getStudentCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getStudentCountPromise]).then(([students, studentsCount]) => {
      if (requestId !== this.itemsSearchRequestId) return;
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        isItemsLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsDirection: '',
        sortItemsColumnsName: ''
      });
    });
  };

  onItemsSearchChange = (itemsSearch: string) => {
    this.setState({ itemsSearch, isItemsLoading: true }, () => this.itemsOnApplyFilterClick(true));
  };

  itemsAccumulatorOnApplyFilterClick = () => {};
  // itemsAccumulatorOnApplyFilterClick = () => {
  //   const { itemsAccumulatorFilters, itemsRemoveBuffer, forms } = this.state;
  //   this.setState({
  //     isLoading: true
  //   });
  //
  //   const options = { forms };
  //
  //   let where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);
  //   const ninIds = itemsRemoveBuffer.map(item => item.id);
  //   propz.set(where, ['id', '$nin'], ninIds);
  //
  //   const { user } = this.props;
  //
  //   const eventId = this.getEventId();
  //
  //   const getPlayersPromise = getAllSchoolEventIndividuals(user, eventId, where);
  //   const getPlayersCountPromise = getSchoolEventPlayersCount(user, eventId, where);
  //
  //   const promiseItems = [getPlayersPromise, getPlayersCountPromise];
  //
  //   BPromise.all(promiseItems).then(([players, playersCount]) => {
  //     const playersNormalized = this.getPlayersNormalized(players);
  //
  //     this.setState({
  //       itemsAccumulator: playersNormalized,
  //       isLoading: false,
  //       itemsAccumulatorCount: playersCount.count,
  //       itemsAccumulatorSelected: [],
  //       itemsSelected: []
  //     });
  //   });
  // };

  itemsOnClearFilterClick = () => {
    const { itemsAccumulatorInitial, itemsAddBuffer, forms } = this.state;
    const { event } = this.props;

    const defaultItemsFilter = this.getDefaultItemsFilter(event);

    this.setState({
      isLoading: true,
      itemsFilters: defaultItemsFilter
    });

    const { user } = this.props;
    const { startTime, endTime, players, id } = event;

    const {
      gender: itemsFiltersGender,
      ages: itemsFiltersAges = [],
      firstName: itemsFiltersFirstName = '',
      lastName: itemsFiltersLastName = ''
    } = defaultItemsFilter;

    let where = {
      eventId: id,
      gender: {
        $in: itemsFiltersGender
      }
    };

    const playerUserIds = players.map(player => ({
      id: player.userId
    }));

    const allItems = [...itemsAccumulatorInitial, ...itemsAddBuffer, ...playerUserIds];
    const ninIds = Lazy(allItems)
      .map(item => item.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    const ages = itemsFiltersAges.length > 0 ? itemsFiltersAges : AGE_GROUPS.ENGLISH.map((age, index) => index);
    const formIds = forms.filter(form => ages.some(age => Number(form.age) === Number(age))).map(form => form.id);

    if (formIds.length > 0) {
      propz.set(where, ['formId', '$in'], formIds);
    }

    let and: { firstName?: { like: any; options: string }; lastName?: { like: any; options: string } }[] = [];

    if (itemsFiltersFirstName.length > 0) {
      and.push({ firstName: { like: itemsFiltersFirstName, options: 'i' } });
    }

    if (itemsFiltersLastName.length > 0) {
      and.push({ lastName: { like: itemsFiltersLastName, options: 'i' } });
    }

    if (and.length > 0) {
      propz.set(where, ['$and'], and);
    }

    // copy-paste from old frontend
    if (!startTime || !endTime) return;

    const startTimeDate = new Date(startTime);
    const startTimeCopy = new Date(startTime);
    startTimeCopy.setHours(startTimeDate.getHours() - 1);
    const intervalClashStartDate = Moment(startTimeCopy)
      .utc()
      .format();

    const endTimeDate = new Date(endTime);
    const endTimeCopy = new Date(endTime);
    endTimeCopy.setHours(endTimeDate.getHours() + 1);
    const intervalClashEndDate = Moment(endTimeCopy)
      .utc()
      .format();

    propz.set(where, ['intervalClashStartDate'], intervalClashStartDate);
    propz.set(where, ['intervalClashEndDate'], intervalClashEndDate);
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    const filter = {
      where: where,
      limit: LIMIT,
      skip: DEFAULT_SKIP,
      order: DEFAULT_INDIVIDUAL_PLAYERS_ORDER
    };

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: '',
        sortItemsDirection: ''
      });
    });
  };

  itemsAccumulatorOnClearFilterClick = () => {};
  // itemsAccumulatorOnClearFilterClick = () => {
  //   this.setState({
  //     isLoading: true
  //   });
  //
  //   const { user } = this.props;
  //   const { itemsRemoveBuffer } = this.state;
  //
  //   const eventId = this.getEventId();
  //
  //   const getPlayersPromise = getAllSchoolEventIndividuals(user, eventId);
  //   const getPlayersCountPromise = getSchoolEventPlayersCount(user, eventId);
  //
  //   const promiseItems = [getPlayersPromise, getPlayersCountPromise];
  //
  //   BPromise.all(promiseItems).then(([players, playersCount]) => {
  //     const playersNormalized = this.getPlayersNormalized(players);
  //     const nextPlayersNormalized = playersNormalized.filter(playerNormalized =>
  //       itemsRemoveBuffer.every(item => item.id !== playerNormalized.id)
  //     );
  //
  //     this.setState({
  //       itemsAccumulator: nextPlayersNormalized,
  //       isLoading: false,
  //       itemsAccumulatorCount: playersCount.count,
  //       itemsAccumulatorSelected: [],
  //       itemsSelected: [],
  //       itemsAccumulatorFilters: {}
  //     });
  //   });
  // };

  onSelectAllItemsClick = () => {
    const { itemsSelected, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    this.setState({
      isLoading: true
    });

    const { user } = this.props;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer, true);

    getSchoolStudentsWithoutClashes(user, filter).then(items => {
      const itemsFiltered = items.filter(item => {
        return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
      });

      const studentsNormalizedFiltered = this.getStudentsNormalized(itemsFiltered);

      const nextSelectedItems = [...itemsSelected, ...studentsNormalizedFiltered];

      this.setState({
        itemsSelected: nextSelectedItems,
        isSelectAllItemsChecked: true,
        itemsAccumulatorSelected: [],
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsOnPageClick = () => {
    const { items, itemsSelected } = this.state;

    const itemsFiltered = items.filter(item => {
      return itemsSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsSelected, ...itemsFiltered];

    this.setState({
      itemsSelected: nextSelectedItems,
      isSelectAllItemsChecked: true,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onUnselectAllItemsClick = () => {
    this.setState({
      itemsSelected: [],
      isSelectAllItemsChecked: false,
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false
    });
  };

  onSelectAllItemsAccumulatorClick = () => {
    const { itemsAccumulatorSelected, itemsAccumulatorFilters, itemsRemoveBuffer, forms } = this.state;

    this.setState({
      isLoading: true
    });

    const { user, event } = this.props;

    const eventId = this.getEventId();
    const options = { forms };
    const where = getServerFieldSectionWhere(itemsAccumulatorFilters, options);

    getIndividualsMethod(user)(user, eventId, { where }).then(players => {
      const playersNormalized = this.getPlayersNormalized(players);

      const playersFiltered = playersNormalized.filter(player => {
        return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== player.id);
      });

      const nextSelectedItems = [...itemsAccumulatorSelected, ...playersFiltered].filter(item => {
        return itemsRemoveBuffer.every(itemRemoveBuffer => itemRemoveBuffer.id !== item.id);
      });

      this.setState({
        itemsAccumulatorSelected: nextSelectedItems,
        isSelectAllItemsAccumulatorChecked: true,
        itemsSelected: [],
        isSelectAllItemsChecked: false,
        isLoading: false
      });
    });
  };

  onSelectAllItemsAccumulatorOnPageClick = () => {
    const { itemsAccumulator, itemsAccumulatorSelected, itemsRemoveBuffer } = this.state;

    const itemsFiltered = itemsAccumulator.filter(item => {
      return itemsAccumulatorSelected.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...itemsAccumulatorSelected, ...itemsFiltered].filter(item => {
      return itemsRemoveBuffer.every(itemRemoveBuffer => itemRemoveBuffer.id !== item.id);
    });

    this.setState({
      itemsAccumulatorSelected: nextSelectedItems,
      isSelectAllItemsAccumulatorChecked: true,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onUnselectAllItemsAccumulatorClick = () => {
    this.setState({
      itemsAccumulatorSelected: [],
      isSelectAllItemsAccumulatorChecked: false,
      itemsSelected: [],
      isSelectAllItemsChecked: false
    });
  };

  onSortItemsClick = (sortField: string) => {
    const { sortItemsDirection, sortItemsColumnsName, itemCurrentPage, itemsAccumulator, itemsAddBuffer } = this.state;

    const order = getOrder(sortField, sortItemsDirection, sortItemsColumnsName) as TABLE_SORT_DIRECTION;

    const filter = this.getItemsFilter(itemCurrentPage, itemsAccumulator, itemsAddBuffer);
    const { user } = this.props;

    const method = getStudentsMethod(user);
    const getStudentsPromise = method(user, filter);
    const getSchoolsCountPromise = getSchoolStudentsWithClashesCount(user, filter);

    Promise.all([getStudentsPromise, getSchoolsCountPromise]).then(([students, studentsCount]) => {
      const studentsNormalized = this.getStudentsNormalized(students);

      this.setState({
        items: studentsNormalized,
        isLoading: false,
        itemsCount: studentsCount.count,
        itemsAccumulatorSelected: [],
        itemsSelected: [],
        itemCurrentPage: 1,
        sortItemsColumnsName: sortField,
        sortItemsDirection: order
      });
    });
  };

  onSortItemsAccumulatorClick = (sortField: string) => {};

  onClashClick = (student: SchoolStudentClash) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = student;
    const { fullClashEvents, intervalClashEvents } = clashes;
    const fullClashEventIds = fullClashEvents.map(fullClashEvent => fullClashEvent._id);
    const intervalClashEventIds = intervalClashEvents.map(intervalClashEvent => intervalClashEvent._id);

    const allPlayerEventIds = [...fullClashEventIds, ...intervalClashEventIds];
    const allPlayerEventIdsUniq = Lazy(allPlayerEventIds)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true
    });

    const eventsPromise: Promise<SchoolEvent>[] = allPlayerEventIdsUniq.map(eventId => getSchoolEvent(user, eventId));

    Promise.all(eventsPromise).then(events => {
      const eventNames = events
        .map((event, index) => {
          const title = getEventTitle(event, user);
          const clash = getEventClash(event, fullClashEventIds, intervalClashEventIds);
          return `${index + 1}. ${title} ${clash}`;
        })
        .join('\n ');

      this.setState({
        isLoading: false,
        studentEventsModalText: `${firstName} ${lastName} is participating in the following events: \n${eventNames}`,
        isStudentEventsModalOpen: true
      });
    });
  };

  onCloseStudentEventsModalClick = () => {
    this.setState({
      isStudentEventsModalOpen: false
    });
  };

  onCloseMaxPlayersModal = () => {
    this.setState({ isMaxPlayersModalOpen: false, maxPlayersModalMessage: '' });
  };

  onHelpModalOpen = () => this.setState({ isHelpModalOpen: true });
  onHelpModalClose = () => this.setState({ isHelpModalOpen: false });

  onRequestClose = () => {
    const { itemsAddBuffer, itemsRemoveBuffer } = this.state;
    const hasChanges = itemsAddBuffer.length > 0 || itemsRemoveBuffer.length > 0;
    if (hasChanges) {
      this.setState({ isExitConfirmationModalOpen: true });
    } else {
      this.props.onCancelClick();
    }
  };

  onExitConfirm = () => {
    this.setState({ isExitConfirmationModalOpen: false });
    this.props.onCancelClick();
  };

  onExitCancel = () => {
    this.setState({ isExitConfirmationModalOpen: false });
  };

  renderStudentEventsModal(): React.ReactNode {
    const { isStudentEventsModalOpen, studentEventsModalText } = this.state;
    return (
      <SimpleModal
        isOpen={isStudentEventsModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentParticipantsNoticeModalHost"
        dialogStyle={{ maxWidth: 520 }}
        onBackdropClick={this.onCloseStudentEventsModalClick}
      >
        <section
          className="tournamentParticipantsNoticeModal isInfo"
          role="dialog"
          aria-label="Participant event clashes"
        >
          <header>
            <div className="tournamentParticipantsNoticeModal__icon">i</div>
            <div>
              <h2>Event clash</h2>
              <p>Review this participant’s other events.</p>
            </div>
            <button type="button" onClick={this.onCloseStudentEventsModalClick} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentParticipantsNoticeModal__body">{studentEventsModalText}</div>
          <footer>
            <button type="button" className="isPrimary" onClick={this.onCloseStudentEventsModalClick}>
              OK
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderHelpModal(): React.ReactNode {
    const { isHelpModalOpen } = this.state;
    const { event } = this.props;
    const maxPlayers =
      propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
      propz.get(event, ['tournament', 'maxPlayersPerTeam']);
    const hasLimit = typeof maxPlayers === 'number' && maxPlayers > 0;

    return (
      <HelpModal
        isOpen={isHelpModalOpen}
        title="Edit participants"
        badgeText="Participants"
        subtitle="Review eligible students and move participants into the event."
        onClose={this.onHelpModalClose}
      >
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Eligible students</div>
              <div className="text-muted">
                The right-hand panel shows students who match the event criteria — age, gender and school. Use filters
                to narrow the list by name, form, house or age group.
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Participants</div>
              <div className="text-muted">
                Move students into the participants panel to add them to the event. Changes are staged on screen until
                you click Save.
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Show younger</div>
              <div className="text-muted">
                Toggle "Show younger" to also see students from younger age groups who may still be eligible to
                participate.
              </div>
            </div>
          </div>
          {hasLimit && (
            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Participant limit</div>
                <div className="text-muted">
                  This sport has a maximum of <strong>{maxPlayers}</strong> participants per school. You will not be
                  able to add more than this number to the event.
                </div>
              </div>
            </div>
          )}
          <div className="col-md-6 mb-3">
            <div className="border rounded p-3 h-100">
              <div className="font-weight-bold mb-2">Saving changes</div>
              <div className="text-muted">
                Click Save when the participant list looks right. Use Cancel to leave without applying staged changes —
                you will be warned if you have unsaved changes.
              </div>
            </div>
          </div>
        </div>
      </HelpModal>
    );
  }

  renderExitConfirmationModal(): React.ReactNode {
    const { isExitConfirmationModalOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isExitConfirmationModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentParticipantsExitModalHost"
        dialogStyle={{ maxWidth: 520 }}
        onBackdropClick={this.onExitCancel}
      >
        <section className="tournamentParticipantsExitModal" role="dialog" aria-label="Discard participant changes">
          <header>
            <div>
              <h2>Leave without saving?</h2>
              <p>Your participant changes have not been saved.</p>
            </div>
            <button type="button" onClick={this.onExitCancel} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentParticipantsExitModal__body">
            If you leave now, the changes made to this event will be lost.
          </div>
          <footer>
            <button type="button" onClick={this.onExitCancel}>
              Keep editing
            </button>
            <button type="button" className="isDanger" onClick={this.onExitConfirm}>
              Leave without saving
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderMaxPlayersModal(): React.ReactNode {
    const { isMaxPlayersModalOpen, maxPlayersModalMessage } = this.state;
    return (
      <SimpleModal
        isOpen={isMaxPlayersModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentParticipantsNoticeModalHost"
        dialogStyle={{ maxWidth: 500 }}
        onBackdropClick={this.onCloseMaxPlayersModal}
      >
        <section
          className="tournamentParticipantsNoticeModal isWarning"
          role="alertdialog"
          aria-label="Player limit reached"
        >
          <header>
            <div className="tournamentParticipantsNoticeModal__icon">!</div>
            <div>
              <h2>Player limit reached</h2>
              <p>The participant limit for this event has been reached.</p>
            </div>
            <button type="button" onClick={this.onCloseMaxPlayersModal} aria-label="Close">
              ×
            </button>
          </header>
          <div className="tournamentParticipantsNoticeModal__body">{maxPlayersModalMessage}</div>
          <footer>
            <button type="button" className="isPrimary" onClick={this.onCloseMaxPlayersModal}>
              OK
            </button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  render() {
    const {
      items,
      isLoading,
      itemsSelected,
      itemsAccumulatorSelected,
      itemsAccumulator,
      itemsFilters,
      isShowItemsFilter,
      isItemsFiltered,
      itemsCount,
      itemsAccumulatorCount,
      itemCurrentPage,
      itemsAccumulatorFilters,
      isShowItemsAccumulatorFilter,
      isItemsAccumulatorFiltered,
      isSelectAllItemsChecked,
      isSelectAllItemsAccumulatorChecked,
      sortItemsDirection,
      sortItemsColumnsName,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName,
      itemsAddBuffer,
      itemsRemoveBuffer,
      forms,
      houses,
      isStudentEventsModalOpen,
      isShowYounger
    } = this.state;

    if (isLoading) {
      return (
        <div className="tournamentEventParticipants tournamentEventParticipants--loading">
          <Loader />
        </div>
      );
    }

    const { user, event, onCancelClick } = this.props;
    const { activeSchool } = user;
    const { ageGroupsNaming, isShowStudentEventClashes } = activeSchool;

    const eventStartDateAndTimeFormatted = getEventStartDateAndTime(event);
    const eventSportName = propz.get(event, ['sport', 'name'], '');
    const eventGender = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender];
    const eventAges = formatTeamAgesCompact(event.ages || [], resolveAgeNamingFromSchool(user.activeSchool));
    const eventTitle = getEventTitle(event, user).replace(getSchoolEventAges(event, user), eventAges);
    const summaryTitle = [eventSportName, eventGender, eventAges].filter(Boolean).join(' · ');

    const maxPlayers =
      propz.get(event, ['sport', 'defaultLimits', 'maxPlayers']) ||
      propz.get(event, ['tournament', 'maxPlayersPerTeam']);
    const hasLimit = typeof maxPlayers === 'number' && maxPlayers > 0;

    const filterForms = this.getFormsFiltered(event.ages || [], forms, !isShowYounger);
    const itemsFiltersOptions = {
      ages: getSelectOptionForAge(filterForms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      forms: getSelectOptionForForms(filterForms),
      houses: getSelectOptionForHouses(houses)
    };

    const classes = isStudentEventsModalOpen ? 'tournamentEventParticipants modal-open' : 'tournamentEventParticipants';

    return (
      <div className={classes}>
        {this.renderStudentEventsModal()}
        {this.renderHelpModal()}
        {this.renderExitConfirmationModal()}
        {this.renderMaxPlayersModal()}
        {this.state.slotCandidates.length > 0 && (
          <SlotAssignmentModal
            isOpen
            event={event}
            newStudents={this.state.slotCandidates.map(item => ({ ...item, userId: item.id })) as any}
            currentParticipants={this.state.itemsAccumulator.map(item => ({
              ...item,
              id: item.playerId || item.id,
              userId: item.id
            }))}
            maxPlayers={maxPlayers}
            activeSchoolId={user.activeSchoolId}
            onConfirm={this.onSlotAssignmentConfirm}
            onClose={() => this.setState({ slotCandidates: [] })}
          />
        )}

        {this.state.participationLimitWarnings.length > 0 && (
          <div className="tournamentEventParticipants__limitWarning" role="alert">
            <strong>Participation limit exceeded</strong>
            <p>You cannot save until the over-limit participant(s) are returned to Eligible students.</p>
            {this.state.participationLimitWarnings.map(message => (
              <div key={message}>{message}</div>
            ))}
          </div>
        )}

        {/* Header */}
        <div className="bSchoolTeamManager__header tournamentEventParticipants__header">
          <div className="bSchoolTeamManager__summaryCard">
            <div className="bSchoolTeamManager__summaryMain">
              <div className="sitPageTitleWithHelp">
                <h2 className="tournamentEventParticipants__title">Edit participants — {eventTitle}</h2>
                <HelpIconButton onClick={this.onHelpModalOpen} label="How tournament individual participants work" />
              </div>
              <div className="bSchoolTeamManager__summaryDate">{eventStartDateAndTimeFormatted}</div>
              <div className="bSchoolTeamManager__summaryTitle">{summaryTitle}</div>
              <div className="bSchoolTeamManager__summaryText">
                Update the participants for this event. Changes are staged here and applied when you save.
              </div>
              {hasLimit && (
                <div className="bSchoolTeamManager__summaryText mt-1" style={{ color: '#f0ad4e', fontWeight: 600 }}>
                  Max {maxPlayers} participants per school
                </div>
              )}
              <div className="mt-2">
                <Switch
                  value={isShowYounger}
                  name={'isShowYounger'}
                  onChange={this.onShowYoungerChange}
                  text={'Show younger'}
                />
              </div>
            </div>
            <button
              type="button"
              className="tournamentEventParticipants__close"
              onClick={this.onRequestClose}
              title="Close"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <section className="tournamentEventParticipants__editor">
          <TwoPanelEditor
            isSourceFirst={true}
            itemsModernPagination={true}
            itemsHiddenFilterChipFields={['forms']}
            itemsSearchValue={this.state.itemsSearch}
            onItemsSearchChange={this.onItemsSearchChange}
            isItemsLoading={this.state.isItemsLoading}
            clashEventsById={this.state.clashEventsById}
            itemsAccumulatorCapacity={hasLimit ? maxPlayers : undefined}
            items={items}
            itemsTitle={'Eligible students'}
            itemsColumns={isShowStudentEventClashes ? ITEMS_COLUMNS_WITH_CLASHES : ITEMS_COLUMNS}
            itemsSelected={itemsSelected}
            onItemClick={this.onItemClick}
            onItemsFilterChange={this.onItemsFilterChange}
            itemsFilters={itemsFilters}
            itemsFiltersOptions={itemsFiltersOptions}
            isShowItemsFilter={isShowItemsFilter}
            isItemsFiltered={isItemsFiltered}
            onItemsFilterClick={this.onItemsFilterClick}
            itemsAccumulator={itemsAccumulator}
            itemsAccumulatorTitle={'Participants'}
            itemsAccumulatorColumns={
              isShowStudentEventClashes ? ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES : ITEMS_ACCUMULATOR_COLUMNS
            }
            onItemAccumulatorClick={this.onItemAccumulatorClick}
            itemsAccumulatorSelected={itemsAccumulatorSelected}
            onItemsAccumulatorFilterChange={this.onItemsAccumulatorFilterChange}
            itemsAccumulatorFilters={itemsAccumulatorFilters}
            itemsAccumulatorFiltersOptions={{}}
            isShowItemsAccumulatorFilter={isShowItemsAccumulatorFilter}
            isItemsAccumulatorFiltered={isItemsAccumulatorFiltered}
            onItemsAccumulatorFilterClick={this.onItemsAccumulatorFilterClick}
            onAddClick={this.onAddClick}
            onRemoveClick={this.onRemoveClick}
            itemsCount={itemsCount}
            itemsAccumulatorCount={itemsAccumulatorCount}
            itemAccumulatorCurrentPage={1}
            itemCurrentPage={itemCurrentPage}
            itemsSetCurrentPageParams={this.itemsSetCurrentPageParams}
            itemsOnApplyFilterClick={this.itemsOnApplyFilterClick}
            itemsOnClearFilterClick={this.itemsOnClearFilterClick}
            itemsAccumulatorOnApplyFilterClick={this.itemsAccumulatorOnApplyFilterClick}
            itemsAccumulatorOnClearFilterClick={this.itemsAccumulatorOnClearFilterClick}
            onSelectAllItemsClick={this.onSelectAllItemsClick}
            onSelectAllItemsOnPageClick={this.onSelectAllItemsOnPageClick}
            onUnselectAllItemsClick={this.onUnselectAllItemsClick}
            isSelectAllItemsChecked={isSelectAllItemsChecked}
            onSelectAllItemsAccumulatorClick={this.onSelectAllItemsAccumulatorClick}
            onSelectAllItemsAccumulatorOnPageClick={this.onSelectAllItemsAccumulatorOnPageClick}
            onUnselectAllItemsAccumulatorClick={this.onUnselectAllItemsAccumulatorClick}
            isSelectAllItemsAccumulatorChecked={isSelectAllItemsAccumulatorChecked}
            sortItemsDirection={sortItemsDirection}
            sortItemsColumnsName={sortItemsColumnsName}
            onSortItemsClick={this.onSortItemsClick}
            sortItemsAccumulatorDirection={sortItemsAccumulatorDirection}
            sortItemsAccumulatorColumnsName={sortItemsAccumulatorColumnsName}
            onSortItemsAccumulatorClick={this.onSortItemsAccumulatorClick}
            itemsAddBuffer={itemsAddBuffer}
            itemsRemoveBuffer={itemsRemoveBuffer}
            onCellItemsClick={this.onClashClick}
            onCellItemsAccumulatorClick={this.onClashClick}
          />
        </section>

        {/* Footer */}
        <div className="bSchoolTeamManager__footer tournamentEventParticipants__footer">
          <div className="bSchoolTeamManager__footerRight">
            <Button
              onClick={this.onRequestClose}
              text={'Cancel'}
              customClass={'sit-btn sit-btn--secondary bSchoolTeamManager__btnSecondary'}
            />
            <Button
              text={'Save participants'}
              onClick={this.onClickSave}
              disabled={this.state.isCheckingParticipationLimits || this.state.participationLimitWarnings.length > 0}
              customClass={'sit-btn sit-btn--primary bSchoolTeamManager__btnPrimary'}
            />
          </div>
        </div>
      </div>
    );
  }
}
